import { useEffect, useState, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface LightningBolt {
  id: number;
  x: number;
  path: string;
}

interface LightningProps {
  /** Interval in ms between bolt attempts (default: 600) */
  interval?: number;
  /** Probability threshold - higher means less frequent (default: 0.4, range 0-1) */
  probability?: number;
  /** Gradient colors [top, middle, bottom] */
  colors?: [string, string, string];
  /** Whether to allow multi-bolt strikes (default: true) */
  multiBolts?: boolean;
}

export default function Lightning({ 
  interval: intervalMs = 600,
  probability = 0.4,
  colors = ['#00d4ff', '#8b5cf6', '#ec4899'],
  multiBolts = true,
}: LightningProps) {
  const [bolts, setBolts] = useState<LightningBolt[]>([]);
  const uniqueId = useId();
  const gradientId = `lightning-gradient-${uniqueId}`;
  const filterId = `glow-${uniqueId}`;

  useEffect(() => {
    const createBolt = () => {
      const x = Math.random() * 100;
      const segments = 8 + Math.floor(Math.random() * 6);
      let path = `M ${x} 0`;
      let currentX = x;
      let currentY = 0;
      
      for (let i = 0; i < segments; i++) {
        const newX = currentX + (Math.random() - 0.5) * 15;
        const newY = currentY + (100 / segments);
        path += ` L ${newX} ${newY}`;
        currentX = newX;
        currentY = newY;
      }

      const bolt: LightningBolt = {
        id: Date.now() + Math.random(),
        x,
        path,
      };

      setBolts(prev => [...prev, bolt]);

      setTimeout(() => {
        setBolts(prev => prev.filter(b => b.id !== bolt.id));
      }, 200);
    };

    const interval = setInterval(() => {
      if (Math.random() > probability) {
        createBolt();
        // Sometimes create multiple bolts for dramatic effect
        if (multiBolts && Math.random() > 0.6) {
          setTimeout(() => createBolt(), 50);
        }
        if (multiBolts && Math.random() > 0.8) {
          setTimeout(() => createBolt(), 100);
        }
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }, [intervalMs, probability, multiBolts]);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <defs>
          <filter id={filterId}>
            <feGaussianBlur stdDeviation="1" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colors[0]} />
            <stop offset="50%" stopColor={colors[1]} />
            <stop offset="100%" stopColor={colors[2]} />
          </linearGradient>
        </defs>
        <AnimatePresence>
          {bolts.map(bolt => (
            <motion.path
              key={bolt.id}
              d={bolt.path}
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth="0.3"
              filter={`url(#${filterId})`}
              initial={{ opacity: 0, pathLength: 0 }}
              animate={{ opacity: [0, 1, 1, 0], pathLength: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            />
          ))}
        </AnimatePresence>
      </svg>
    </div>
  );
}

